import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { isDemoMode } from '../services/dataScope';
import { paymentsService } from '../services/supabase';

/** Custom event dispatched when payment due status has been synced (sent ↔ overdue) */
export const PAYMENTS_DUE_STATUS_UPDATED = 'payments-due-status-updated';

/** Event to trigger a due status check after payment create/update (e.g. due date changed) */
const PAYMENT_MUTATED = 'payment-mutated';

const DEBOUNCE_MS = 400;

export interface PaymentsDueStatusUpdatedDetail {
  toOverdueIds: string[];
  toSentIds: string[];
}

/** Call this after create/update/markAsPaid to re-check sent↔overdue transitions */
export function notifyPaymentMutated(): void {
  window.dispatchEvent(new CustomEvent(PAYMENT_MUTATED));
}

/**
 * Hook that runs payment due status check:
 * - On first load (Layout mount) when user is authenticated
 * - After payment mutations (create, update, mark as paid) via notifyPaymentMutated()
 */
export function usePaymentDueStatusSync(): void {
  const { user } = useAuth();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runCheck = useCallback(async () => {
    if (!user?.id || isDemoMode()) return;
    try {
      const today = new Date();
      const { toOverdueIds, toSentIds } = await paymentsService.getDueStatusTransitions(today);

      if (toOverdueIds.length > 0) {
        await Promise.all(
          toOverdueIds.map((id) => paymentsService.update(id, { status: 'overdue' }))
        );
      }

      if (toSentIds.length > 0) {
        await Promise.all(
          toSentIds.map((id) => paymentsService.update(id, { status: 'sent' }))
        );
      }

      if (toOverdueIds.length > 0 || toSentIds.length > 0) {
        const detail: PaymentsDueStatusUpdatedDetail = { toOverdueIds, toSentIds };
        window.dispatchEvent(new CustomEvent(PAYMENTS_DUE_STATUS_UPDATED, { detail }));
      }
    } catch (error) {
      console.error('Error syncing payment due status:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    // First load
    runCheck();

    // After payment mutations (debounced)
    const handler = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        runCheck();
      }, DEBOUNCE_MS);
    };

    window.addEventListener(PAYMENT_MUTATED, handler);
    return () => {
      window.removeEventListener(PAYMENT_MUTATED, handler);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [runCheck, user?.id]);
}
